import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { delegationsApi } from '../../../api/admin/delegations';
import { usersApi } from '../../../api/admin/users';
import { ApiError } from '../../../api/client';
import { StatusBadge } from '../../../components/StatusBadge';
import { IconActionButton } from '../../../components/IconActionButton';
import { Ban } from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable } from '../../../components/DataTable';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';

const formSchema = z.object({
  delegatorUserId: z.coerce.number().int().positive('Select a delegator.'),
  delegateUserId: z.coerce.number().int().positive('Select a delegate.'),
  startDate: z.string().min(1, 'Start date is required.'),
  endDate: z.string().min(1, 'End date is required.'),
  reason: z.string().optional(),
});
type FormValues = z.infer<typeof formSchema>;

export function DelegationsPage() {
  const canManage = usePermission('admin.delegations.manage');
  const [modalOpen, setModalOpen] = useState(false);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const delegationsQuery = useQuery({ queryKey: ['delegations'], queryFn: () => delegationsApi.list() });
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list() });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(formSchema) });

  const createMutation = useMutation({
    mutationFn: (values: FormValues) => delegationsApi.create({ ...values, reason: values.reason || null }),
    onSuccess: () => {
      showSuccess('Delegation created.');
      queryClient.invalidateQueries({ queryKey: ['delegations'] });
      setModalOpen(false);
      reset();
    },
    // Surfaces the real guard messages: self-delegation, inactive
    // user, or circular delegation chain.
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create delegation.'),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: number) => delegationsApi.cancel(id),
    onSuccess: () => {
      showSuccess('Delegation cancelled.');
      queryClient.invalidateQueries({ queryKey: ['delegations'] });
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to cancel delegation.'),
  });

  const userName = (id: number) => usersQuery.data?.data.find((u) => u.id === id)?.full_name ?? `User #${id}`;

  return (
    <div className="page">
      <PageHeader title="Delegations" actions={canManage && <button className="btn-primary" onClick={() => setModalOpen(true)}>New Delegation</button>} />

      {delegationsQuery.isLoading && <p>Loading…</p>}
      {delegationsQuery.isError && <p className="error-text">Could not load delegations.</p>}

      {delegationsQuery.data && (
        <DataTable
          rows={delegationsQuery.data.data}
          rowKey={(d) => d.id}
          emptyMessage="No delegations yet."
          columns={[
            { header: 'Delegator', render: (d) => userName(d.delegator_user_id) },
            { header: 'Delegate', render: (d) => userName(d.delegate_user_id) },
            { header: 'Period', render: (d) => `${d.start_date} → ${d.end_date}` },
            { header: 'Status', render: (d) => <StatusBadge status={d.status} /> },
            {
              header: '',
              render: (d) =>
                canManage && d.status !== 'cancelled' ? (
                  <IconActionButton icon={Ban} label="Cancel" danger onClick={() => cancelMutation.mutate(d.id)} />
                ) : null,
            },
          ]}
        />
      )}

      {modalOpen && (
        <Modal title="New Delegation" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>
              Delegator
              <select {...register('delegatorUserId')}>
                <option value="">Select…</option>
                {usersQuery.data?.data.map((u) => (
                  <option key={u.id} value={u.id}>{u.full_name}</option>
                ))}
              </select>
              {errors.delegatorUserId && <span className="field-error">{errors.delegatorUserId.message}</span>}
            </label>
            <label>
              Delegate
              <select {...register('delegateUserId')}>
                <option value="">Select…</option>
                {usersQuery.data?.data.map((u) => (
                  <option key={u.id} value={u.id}>{u.full_name}</option>
                ))}
              </select>
              {errors.delegateUserId && <span className="field-error">{errors.delegateUserId.message}</span>}
            </label>
            <label>
              Start date
              <input type="date" {...register('startDate')} />
              {errors.startDate && <span className="field-error">{errors.startDate.message}</span>}
            </label>
            <label>
              End date
              <input type="date" {...register('endDate')} />
              {errors.endDate && <span className="field-error">{errors.endDate.message}</span>}
            </label>
            <label>
              Reason (optional)
              <input {...register('reason')} />
            </label>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Saving…' : 'Create'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
